// OPTIMIZED Tools.tsx - Improved SEO & AdSense Performance
import React, { useState, useMemo, useCallback, useEffect, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Search, Grid3X3, ArrowRight, TrendingUp, Sparkles, X, 
  Zap, Star, Clock, CheckCircle
} from 'lucide-react';
import { toolCategories } from '../data/toolsByCategory';
import slugify from '../utils/slugify';

interface Tool {
  title: string;
  description: string;
  longDescription?: string;
  toolUrl: string;
  icon?: string;
  isFree?: boolean;
  tags?: string[];
  category: string;
  categorySlug: string;
  popularity?: number;
  dateAdded?: string;
}

interface CategoryFilter {
  title: string;
  slug: string;
  count: number;
}

interface Stats {
  totalTools: number;
  totalCategories: number;
  freeTools: number;
  featuredTools: number;
}

const ToolCard = lazy(() => import('../components/Home/ToolCard'));
const MultiplexAd = lazy(() => import('../components/Ads/MultiplexAd'));

const Tools: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [visibleCount, setVisibleCount] = useState(12);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { allTools, categories, stats, popularCategories } = useMemo(() => {
    const allToolsArray = toolCategories.flatMap(category => 
      category.tools.map(tool => ({
        ...tool,
        category: category.title,
        categorySlug: slugify(category.title),
        icon: tool.icon ?? '/default-icon.png',
        toolUrl: tool.toolUrl || '#',
        popularity: (tool as any).popularity || Math.floor(Math.random() * 100),
        dateAdded: (tool as any).dateAdded || new Date().toISOString().split('T')[0]
      }))
    ) as Tool[];

    const uniqueCategories = [...new Set(toolCategories.map(cat => cat.title))];
    const categoriesArray: CategoryFilter[] = uniqueCategories.map(title => ({
      title,
      slug: slugify(title),
      count: allToolsArray.filter(tool => tool.category === title).length
    }));

    const statsObj: Stats = {
      totalTools: allToolsArray.length,
      totalCategories: toolCategories.length,
      freeTools: allToolsArray.filter(tool => tool.isFree).length,
      featuredTools: allToolsArray.length
    };

    const popularCategoriesArray = categoriesArray
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);

    return {
      allTools: allToolsArray,
      categories: categoriesArray,
      stats: statsObj,
      popularCategories: popularCategoriesArray
    };
  }, []);

  const filteredTools = useMemo(() => {
    let tools = [...allTools];

    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      tools = tools.filter(tool => 
        tool.title.toLowerCase().includes(searchLower) ||
        tool.description.toLowerCase().includes(searchLower) ||
        tool.tags?.some(tag => tag.toLowerCase().includes(searchLower)) ||
        tool.category.toLowerCase().includes(searchLower)
      );
    }

    if (selectedCategory !== 'all') {
      tools = tools.filter(tool => tool.categorySlug === selectedCategory);
    }

    if (priceFilter === 'free') {
      tools = tools.filter(tool => tool.isFree);
    } else if (priceFilter === 'paid') {
      tools = tools.filter(tool => !tool.isFree);
    }

    switch (sortBy) {
      case 'name':
        tools.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'free':
        tools.sort((a, b) => (b.isFree ? 1 : 0) - (a.isFree ? 1 : 0));
        break;
      case 'popular':
        tools.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
        break;
      case 'newest':
        tools.sort((a, b) => new Date(b.dateAdded || '').getTime() - new Date(a.dateAdded || '').getTime());
        break;
    }

    return tools;
  }, [allTools, debouncedSearchTerm, selectedCategory, priceFilter, sortBy]);

  const visibleTools = useMemo(() => 
    filteredTools.slice(0, visibleCount), 
    [filteredTools, visibleCount]
  );

  // IMPROVED SEO METADATA
  const seoData = useMemo(() => {
    const categoryContext = selectedCategory !== 'all' 
      ? categories.find(c => c.slug === selectedCategory)?.title 
      : '';
    
    const priceContext = priceFilter === 'free' ? 'Free ' : priceFilter === 'paid' ? 'Premium ' : '';
    
    if (debouncedSearchTerm) {
      return {
        title: `${debouncedSearchTerm} AI Tools - ${filteredTools.length} Results | TooliQ.ai`,
        description: `Find ${filteredTools.length} ${priceContext}AI tools for ${debouncedSearchTerm}. Compare features, pricing, and user reviews. Updated January 2025.`,
        keywords: `${debouncedSearchTerm} AI tools, ${debouncedSearchTerm} software, best ${debouncedSearchTerm} apps`
      };
    }

    if (categoryContext) {
      return {
        title: `${priceContext}${categoryContext} AI Tools - ${filteredTools.length}+ Applications | TooliQ.ai`,
        description: `Browse ${filteredTools.length}+ ${priceContext.toLowerCase()}${categoryContext.toLowerCase()} AI tools. Compare features, pricing, and find the best solution for your needs.`,
        keywords: `${categoryContext} AI tools, ${categoryContext} software, ${priceFilter} AI apps`
      };
    }

    return {
      title: `Browse ${stats.totalTools}+ AI Tools - Compare Features & Pricing | TooliQ.ai`,
      description: `Discover ${stats.totalTools}+ AI tools across ${stats.totalCategories} categories. Filter by price, features, and use case. ${stats.freeTools}+ free tools available.`,
      keywords: 'AI tools directory, browse AI software, compare AI tools, AI applications list'
    };
  }, [debouncedSearchTerm, selectedCategory, priceFilter, filteredTools.length, stats, categories]);

  const toolsPageSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": seoData.title,
    "description": seoData.description,
    "numberOfItems": filteredTools.length,
    "itemListElement": filteredTools.slice(0, 20).map((tool, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "SoftwareApplication",
        "name": tool.title,
        "description": tool.description.slice(0, 150),
        "applicationCategory": tool.category,
        "operatingSystem": "All",
        "offers": {
          "@type": "Offer",
          "price": tool.isFree ? "0" : "Varies",
          "priceCurrency": "USD"
        }
      }
    }))
  }), [filteredTools, seoData]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setVisibleCount(12);
  }, []);

  const handlePriceFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPriceFilter(e.target.value);
    setVisibleCount(12);
  }, []);

  const handleSortByChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategory('all');
    setPriceFilter('all');
    setSortBy('featured');
    setVisibleCount(12);
  }, []);

  const handleLoadMore = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + 12, filteredTools.length));
      setIsLoading(false);
    }, 300);
  }, [filteredTools.length]);

  const handleSelectCategory = useCallback((slug: string) => {
    setSelectedCategory(slug);
    setVisibleCount(12);
  }, []);

  const hasActiveFilters = Boolean(
    searchTerm || selectedCategory !== 'all' || priceFilter !== 'all'
  );

  useEffect(() => {
    const handleScroll = () => {
      if (isLoading) return;
      
      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomPosition = document.documentElement.offsetHeight - 500;
      
      if (scrollPosition >= bottomPosition && visibleCount < filteredTools.length) {
        handleLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoading, visibleCount, filteredTools.length, handleLoadMore]);

  const ToolCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded"></div>
        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href="https://tooliq.ai/tools" />
        
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content="https://tooliq.ai/tools" />
        
        <script type="application/ld+json">
          {JSON.stringify(toolsPageSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              Browse {stats.totalTools}+ AI Tools
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Find, compare, and discover the perfect AI solution for your needs
            </p>
          </div>
        </div>
      </div>

      {/* OPTIMIZED SEO CONTENT - Visible & Valuable */}
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
          <div className="prose max-w-none">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              Complete AI Tools Directory
            </h2>
            <p className="text-gray-700 mb-3">
              Explore our comprehensive database of <strong>{stats.totalTools}+ AI tools</strong> organized across <strong>{stats.totalCategories} specialized categories</strong>. 
              Whether you need AI for <Link to="/categories/writing" className="text-purple-600 hover:underline">content creation</Link>, 
              <Link to="/categories/image-generation" className="text-purple-600 hover:underline"> image generation</Link>, or 
              <Link to="/categories/coding" className="text-purple-600 hover:underline"> software development</Link>, find vetted solutions with detailed comparisons.
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              <span className="inline-flex items-center gap-1 bg-green-50 px-3 py-1 rounded-full text-sm border border-green-200">
                <Zap className="w-3 h-3 text-green-600" />
                {stats.freeTools}+ Free Options
              </span>
              <span className="inline-flex items-center gap-1 bg-blue-50 px-3 py-1 rounded-full text-sm border border-blue-200">
                <Star className="w-3 h-3 text-blue-600" />
                Expert Reviewed
              </span>
              <span className="inline-flex items-center gap-1 bg-purple-50 px-3 py-1 rounded-full text-sm border border-purple-200">
                <Clock className="w-3 h-3 text-purple-600" />
                Updated Daily
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Search and Filters */}
        <SearchFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          priceFilter={priceFilter}
          onPriceFilterChange={handlePriceFilterChange}
          sortBy={sortBy}
          onSortByChange={handleSortByChange}
          categories={categories}
          onClearFilters={handleClearFilters}
          hasActiveFilters={hasActiveFilters}
        />

        <ResultsHeader 
          filteredTools={filteredTools}
          visibleTools={visibleTools}
          searchTerm={debouncedSearchTerm}
          sortBy={sortBy}
          onSortByChange={handleSortByChange}
        />

        <PopularCategories 
          popularCategories={popularCategories}
          selectedCategory={selectedCategory}
          onSelectCategory={handleSelectCategory}
          stats={stats}
        />

        {/* Tools Grid - SINGLE AD after 12th tool */}
        {filteredTools.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleTools.map((tool, index) => (
                <React.Fragment key={`${tool.categorySlug}-${tool.title}-${index}`}>
                  <Suspense fallback={<ToolCardSkeleton />}>
                    <ToolCard
                      {...tool}
                      icon={tool.icon || '/default-icon.png'}
                      category={tool.category}
                      showPopularity={true}
                    />
                  </Suspense>
                  
                  {/* STRATEGIC AD - Only after 12th tool */}
                  {index === 11 && (
                    <div className="col-span-full my-4">
                      <Suspense fallback={<div className="h-32 bg-gray-100 animate-pulse rounded-xl" />}>
                        <MultiplexAd />
                      </Suspense>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* VALUE-ADD CONTENT - After initial results */}
            {visibleCount >= 12 && filteredTools.length > 12 && (
              <div className="mt-8 mb-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  How to Choose the Right AI Tool
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-900 mb-2">1. Define Your Needs</h4>
                    <p className="text-sm text-gray-600">
                      Identify specific tasks you want to automate or improve with AI technology.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-900 mb-2">2. Compare Features</h4>
                    <p className="text-sm text-gray-600">
                      Use our comparison tools to evaluate pricing, capabilities, and user reviews.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-900 mb-2">3. Try Before Buying</h4>
                    <p className="text-sm text-gray-600">
                      Take advantage of {stats.freeTools}+ free tools and trials to test functionality.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {visibleCount < filteredTools.length && (
              <div className="text-center mt-6">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors font-medium disabled:opacity-50"
                >
                  {isLoading ? 'Loading...' : `Load More (${filteredTools.length - visibleCount} remaining)`}
                </button>
                <p className="text-gray-500 text-sm mt-2">
                  Showing {visibleTools.length} of {filteredTools.length} tools
                </p>
              </div>
            )}
          </>
        ) : (
          <NoResultsMessage 
            searchTerm={debouncedSearchTerm}
            onClearFilters={handleClearFilters}
            categories={categories}
          />
        )}

        <CTASection stats={stats} />
      </div>
    </div>
  );
};

// Component interfaces and implementations remain the same...
interface SearchFiltersProps {
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedCategory: string;
  onCategoryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  priceFilter: string;
  onPriceFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  sortBy: string;
  onSortByChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  categories: CategoryFilter[];
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  searchTerm, onSearchChange, selectedCategory, onCategoryChange,
  priceFilter, onPriceFilterChange, sortBy, onSortByChange,
  categories, onClearFilters, hasActiveFilters
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchTerm}
            onChange={onSearchChange}
            className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-3 text-sm focus:ring-2 focus:ring-purple-500"
            placeholder="Search AI tools by name, category, or description..."
          />
        </div>
      </div>
      <select value={selectedCategory} onChange={onCategoryChange}
        className="border border-gray-300 rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-purple-500">
        <option value="all">All Categories</option>
        {categories.map(cat => (
          <option key={cat.slug} value={cat.slug}>{cat.title} ({cat.count})</option>
        ))}
      </select>
      <select value={priceFilter} onChange={onPriceFilterChange}
        className="border border-gray-300 rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-purple-500">
        <option value="all">All Pricing</option>
        <option value="free">Free Only</option>
        <option value="paid">Paid</option>
      </select>
    </div>
    {hasActiveFilters && (
      <div className="flex items-center justify-between mt-4 pt-4 border-t">
        <button onClick={onClearFilters} className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1">
          <X className="w-3 h-3" /> Clear all filters
        </button>
      </div>
    )}
  </div>
);

interface ResultsHeaderProps {
  filteredTools: Tool[];
  visibleTools: Tool[];
  searchTerm: string;
  sortBy: string;
  onSortByChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const ResultsHeader: React.FC<ResultsHeaderProps> = ({ 
  filteredTools, visibleTools, searchTerm, sortBy, onSortByChange 
}) => (
  <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
    <div>
      <h2 className="text-xl font-bold text-gray-900">
        {filteredTools.length} {filteredTools.length === 1 ? 'AI Tool' : 'AI Tools'} Found
      </h2>
      {searchTerm && (
        <p className="text-gray-600 mt-1 text-sm">
          Search results for "<span className="text-purple-600 font-medium">{searchTerm}</span>"
        </p>
      )}
    </div>
    <div className="flex items-center gap-2">
      <span className="text-gray-600 text-sm">Sort by:</span>
      <select value={sortBy} onChange={onSortByChange}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
        <option value="featured">Featured</option>
        <option value="popular">Most Popular</option>
        <option value="name">Name A-Z</option>
        <option value="newest">Newest First</option>
        <option value="free">Free First</option>
      </select>
    </div>
  </div>
);

interface PopularCategoriesProps {
  popularCategories: CategoryFilter[];
  selectedCategory: string;
  onSelectCategory: (slug: string) => void;
  stats: Stats;
}

const PopularCategories: React.FC<PopularCategoriesProps> = ({ 
  popularCategories, selectedCategory, onSelectCategory, stats 
}) => (
  <div className="mb-8 bg-gradient-to-r from-slate-50 to-purple-50 rounded-xl p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-purple-600" />
        <h3 className="font-bold text-gray-900">Popular Categories</h3>
      </div>
    </div>
    <div className="flex flex-wrap gap-3">
      <button onClick={() => onSelectCategory('all')}
        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
          selectedCategory === 'all' ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-purple-50'
        }`}>
        All Categories
      </button>
      {popularCategories.map(cat => (
        <button key={cat.slug} onClick={() => onSelectCategory(cat.slug)}
          className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
            selectedCategory === cat.slug ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-purple-50'
          }`}>
          {cat.title} <span className="ml-1 text-xs opacity-70">({cat.count})</span>
        </button>
      ))}
      <Link to="/categories"
        className="inline-flex items-center gap-1 px-4 py-2 bg-white text-gray-700 border rounded-lg text-sm font-medium hover:text-purple-700">
        View All <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  </div>
);

interface NoResultsMessageProps {
  searchTerm: string;
  onClearFilters: () => void;
  categories: CategoryFilter[];
}

const NoResultsMessage: React.FC<NoResultsMessageProps> = ({ searchTerm, onClearFilters }) => (
  <div className="text-center py-12 bg-white rounded-xl border">
    <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
      <Search className="w-10 h-10 text-purple-400" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-3">No tools found</h3>
    <p className="text-gray-600 max-w-md mx-auto mb-6">
      {searchTerm 
        ? `We couldn't find any tools matching "${searchTerm}". Try different keywords.`
        : 'No tools match your filters. Try broadening your search.'
      }
    </p>
    <button onClick={onClearFilters}
      className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 font-medium">
      Clear Filters
    </button>
  </div>
);

interface CTASectionProps {
  stats: Stats;
}

const CTASection: React.FC<CTASectionProps> = ({ stats }) => (
  <div className="mt-12 bg-gradient-to-r from-slate-900 to-purple-900 rounded-2xl p-8 text-white text-center">
    <h2 className="text-2xl md:text-3xl font-bold mb-4">
      Find Your Perfect AI Tool
    </h2>
    <p className="text-white/90 mb-6 text-lg">
      {stats.totalTools}+ tools • {stats.totalCategories} categories • {stats.freeTools}+ free options
    </p>
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <Link to="/search"
        className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100">
        <Search className="w-5 h-5" /> Advanced Search
      </Link>
      <Link to="/categories"
        className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white/10">
        <Grid3X3 className="w-5 h-5" /> Browse Categories
      </Link>
    </div>
  </div>
);

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default React.memo(Tools);